import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import { useAuthStore } from './auth';
import { loadClientList } from '../services/clientListService';

// Counts client-list loads. Only the latest load may change the store, so an
// older, slower one can't overwrite a newer list or clear it.
let latestClientListLoad = 0;

export const useClientStore = defineStore('client', {
  // loadError is left out on purpose: a stale failure must not toast on reload.
  persist: { paths: ['clients', 'pharmacies', 'specialInstructions', 'treatmentOutcomes'] },

  state: () => ({
    clients: [],
    pharmacies: [],
    specialInstructions: [],
    treatmentOutcomes: [],
    // True after the client list failed to load. MainLayout toasts once and resets it.
    loadError: false,
  }),

  getters: {
    getClient: (state) => (id) => {
      return state.clients.find((client) => client.clientID === id);
    },
  },

  actions: {
    async dataRefresh() {
      await this.fetchTreatmentOutcomes();
      const authStore = useAuthStore();
      if (authStore.isQualityManager || authStore.isDoctor) {
        // Not awaited, so login does not wait for the list. A failure sets
        // loadError, which drives one toast in MainLayout. Swallow it here so
        // the reload card does not toast a second time.
        this.fetchClients().catch(() => {});
      }
    },
    async fetchTreatmentOutcomes() {
      const authStore = useAuthStore();
      this.treatmentOutcomes = await this.fetchClientTreatmentOutcomes(authStore.clientId);
    },
    async fetchClientTreatmentOutcomes(clientID) {
      try {
        const res = await api.get(`/api/v1/clients/${clientID}/treatmentoutcomes`);
        return res.data;
      } catch (err) {
        console.error(err);
      }
    },
    fetchPharmacies(clientID) {
      return new Promise((resolve) => {
        api
          .get('api/v1/clients/' + clientID + '/pharmacies')
          .then((response) => {
            this.pharmacies = response.data;
            resolve(response.data);
          })
          .catch((err) => {
            console.error(err);
            resolve(this.pharmacies);
          });
      });
    },
    fetchSpecialInstructions(clientID) {
      return new Promise((resolve, reject) => {
        api({
          url: 'api/v1/documents/specialinstructions/' + clientID,
          method: 'GET',
          responseType: 'blob',
        })
          .then((response) => {
            this.specialInstructions = response;
            resolve(response);
          })
          .catch((err) => {
            console.error(err);
            reject(err);
          });
      });
    },
    /**
     * Loads the client list from legacy or Erebus (by tenant migration state),
     * stores it and returns it. On failure the list is cleared, loadError is
     * set, and the error is rethrown.
     */
    async fetchClients() {
      const load = ++latestClientListLoad;
      this.loadError = false;
      try {
        const clients = await loadClientList();
        if (load === latestClientListLoad) this.clients = clients;
        return this.clients;
      } catch (err) {
        console.error(err);
        if (load === latestClientListLoad) {
          this.clients = [];
          this.loadError = true;
        }
        throw err;
      }
    },
  },
});
